/**
 * useLastTurn - the LAST TURN, replayed on this screen (plan design-gaps, Z6; ruled: this screen only).
 *
 * While a turn runs, this screen keeps every impact (engine/impacts) and walk (useShellWalks) it is handed, stamped
 * when it first saw each; when the turn passes to someone else that becomes the last turn (engine/display/lastTurn)
 * - a turn in which this screen saw nothing leaves the one before. `replay()` plays it again: each beat and walk
 * falls due at its offset, restamped with a fresh id so the beats take it for new, and is merged over the live
 * impacts and walks the map is handed. Over the figures as they stand now: a replay redraws what happened, it
 * changes nothing. Nothing to replay on a screen that holds still - the map plays no beats there.
 *
 * @param {Record<string, object>|undefined} impacts - the live impacts by combatant (useShellImpacts)
 * @param {Record<string, object>|undefined} walks - the kept moves by actor (useShellWalks)
 * @param {string|null|undefined} turnKey - whose turn it is (the order's actor): a change ends the turn
 * @returns {{ impacts: object|undefined, walks: object|undefined, last: object|null, playing: number|null,
 *   replay: () => void }} the impacts and walks to draw (the live ones, a replay's merged over), the last turn kept,
 *   and when the replay running began (null: none)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { liveImpacts } from '../engine/impacts';
import { dueAt, lastTurnOf } from '../engine/display/lastTurn';

/** The replay's tick: walks and beats fall due on it, so a tenth of a second is near enough. */
const TICK_MS = 100;

export default function useLastTurn(impacts, walks, turnKey) {
  const last = useTurnRecord(impacts, walks, turnKey);
  const [run, setRun] = useState(null);
  const replay = useCallback(() => {
    if (last) setRun((r) => runFrom(last, Date.now(), (r?.n ?? 0) + 1));
  }, [last]);
  useReplayClock(run, setRun);
  // Memoized on the run, which changes only when something new falls due: restamped objects that kept changing
  // identity would make the beats take every render for news (the Track Z simplify).
  const merged = useMemo(() => (run ? replayed(run, impacts, walks) : { impacts, walks }), [run, impacts, walks]);
  return { ...merged, last, playing: run?.startedAt ?? null, replay };
}

/** A replay's state at `at`: what has fallen due by then (`due`, a count, is what the clock watches). */
function runFrom(turn, startedAt, n, at = startedAt) {
  const due = dueAt(turn, at - startedAt);
  return { turn, startedAt, n, at, due, count: due.impacts.length + due.walks.length };
}

/**
 * What the turn running now has shown this screen, and the last one kept when the turn passed. What was already
 * held when the turn passed is the turn before's (a walk is kept 30 s, an impact 2.5 s): it is never recorded into
 * the new one (the Track Z review). Without an order the turn never passes: the record keeps its newest MAX_KEPT,
 * and when an order begins it is dropped, never offered - it was no turn (found filming the Stealth tutorial).
 */
function useTurnRecord(impacts, walks, turnKey) {
  const now = useRef(freshTurn());
  const held = useRef({ impacts, walks });
  held.current = { impacts, walks };
  const [last, setLast] = useState(null);
  useEffect(() => {
    const at = Date.now();
    for (const i of Object.values(impacts ?? {})) note(now.current, 'impacts', i.id, { item: i, seen: at });
  }, [impacts]);
  useEffect(() => {
    for (const m of Object.values(walks ?? {})) note(now.current, 'walks', walkKey(m), { item: m, seen: m.seenAt });
  }, [walks]);
  const turnWas = useRef(turnKey);
  useEffect(() => {
    const was = now.current;
    // Only a turn in an order is a turn: what was played with no order (Stealth, narrative time) is not kept.
    const ordered = turnWas.current != null;
    turnWas.current = turnKey;
    const turn = ordered ? lastTurnOf([...was.impacts.values()], [...was.walks.values()]) : null;
    now.current = freshTurn(held.current);
    if (turn) setLast(turn);
  }, [turnKey]);
  return last;
}

/** How many of each a turn keeps: a session with no order never closes one. */
const MAX_KEPT = 50;
const walkKey = (m) => `${m.actorId}:${m.at}`;

/** A new turn's record: empty, with what is held now marked as the turn before's. */
function freshTurn(held = {}) {
  const before = new Set([
    ...Object.values(held.impacts ?? {}).map((i) => i.id),
    ...Object.values(held.walks ?? {}).map(walkKey),
  ]);
  return { impacts: new Map(), walks: new Map(), before };
}

/** One thing seen, once, unless it was the turn before's; the oldest dropped past MAX_KEPT. */
function note(turn, kind, key, seen) {
  if (turn.before.has(key) || turn[kind].has(key)) return;
  turn[kind].set(key, seen);
  if (turn[kind].size > MAX_KEPT) turn[kind].delete(turn[kind].keys().next().value);
}

/** The replay's clock: elapsed time on a tick while it runs, and the run dropped once its span is over. */
function useReplayClock(run, setRun) {
  const startedAt = run?.startedAt;
  const span = run?.turn.span;
  useEffect(() => {
    if (!startedAt) return undefined;
    const t = setInterval(() => {
      const now = Date.now();
      setRun((r) => {
        if (!r || now - startedAt > span) return null;
        const next = runFrom(r.turn, startedAt, r.n, now);
        // Nothing new fell due: the same run, so nothing downstream redraws on the tick.
        return next.count === r.count ? r : next;
      });
    }, TICK_MS);
    return () => clearInterval(t);
  }, [startedAt, span, setRun]);
}

/** The live impacts and walks with the replay's due ones merged over, each restamped at its own moment. */
function replayed(run, impacts, walks) {
  const { due } = run;
  const again = due.impacts.map(({ item, offset }) => ({
    ...item,
    id: `${item.id}~r${run.n}`,
    at: run.startedAt + offset,
  }));
  const moves = {};
  for (const { item, offset } of due.walks) {
    const at = run.startedAt + offset;
    if (!moves[item.actorId] || moves[item.actorId].at < at) moves[item.actorId] = { ...item, at, seenAt: at };
  }
  return { impacts: { ...impacts, ...liveImpacts(again, run.at) }, walks: { ...walks, ...moves } };
}
